import type { CSSProperties, FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/lib/format'
import { financing, monthlyPayment } from '@/constants/pricing'

export const FinancingCalculator: FC = (): ReactNode => {
    const [amount, setAmount] = useState(financing.start)
    const [months, setMonths] = useState(financing.defaultMonths)
    const sliderId = useId()

    const term = financing.terms.find(option => option.months === months) ?? financing.terms[financing.terms.length - 1]
    const payment = monthlyPayment(amount, term)
    const fill = ((amount - financing.min) / (financing.max - financing.min)) * 100

    return (
        <div className='relative overflow-hidden rounded-[30px] border border-cream/10 bg-graphite/80 p-7 text-cream backdrop-blur sm:p-9'>
            <div className='flex items-end justify-between gap-4'>
                <label htmlFor={sliderId} className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/50'>
                    Amount to finance
                </label>

                <p className='font-heading text-[2rem] font-semibold leading-none tracking-[-0.04em]'>
                    {formatMoney(amount)}
                </p>
            </div>

            <input
                id={sliderId}
                type='range'
                min={financing.min}
                max={financing.max}
                step={financing.step}
                value={amount}
                onChange={event => setAmount(Number(event.target.value))}
                aria-valuetext={formatMoney(amount)}
                className='range-input mt-7'
                style={{ '--fill': `${fill}%` } as CSSProperties}
            />

            <div className='mt-3 flex justify-between font-mono text-[11px] text-cream/40'>
                <span>{formatMoney(financing.min)}</span>
                <span>{formatMoney(financing.max)}</span>
            </div>

            <div className='mt-8 grid grid-cols-4 gap-2' role='group' aria-label="Repayment term">
                {financing.terms.map(option => (
                    <button
                        key={option.months}
                        type='button'
                        aria-pressed={months === option.months}
                        onClick={() => setMonths(option.months)}
                        className={cn(
                            'h-12 rounded-2xl border text-[14px] font-medium transition-colors duration-300',
                            months === option.months ? 'border-cream bg-cream text-ink' : 'border-cream/15 text-cream hover:border-cream/40'
                        )}
                    >
                        {option.months} mo
                    </button>
                ))}
            </div>

            <div className='mt-8 flex items-end justify-between gap-6 border-t border-cream/10 pt-7' aria-live='polite'>
                <div>
                    <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/50'>
                        Estimate per month
                    </p>

                    <p className='mt-2 flex items-baseline gap-1.5'>
                        <span className='font-heading text-[3.4rem] font-semibold leading-none tracking-[-0.05em] text-cream'>
                            {formatMoney(payment)}
                        </span>

                        <span className='text-cream/55'>/mo</span>
                    </p>
                </div>

                <div className='text-right font-mono text-[12px] leading-relaxed text-cream/60'>
                    <p>{term.apr === 0 ? '0%' : `${term.apr}%`} APR</p>
                    <p>{term.months} payments</p>
                </div>
            </div>

            <p className='mt-6 text-[12.5px] leading-relaxed text-cream/40'>
                {financing.fineprint}
            </p>
        </div>
    )
}
